/**
 * LastSyncedBadge — tiny "Last synced 2s ago" indicator with a manual refresh button.
 *
 * Used on every money page (Home / Collections / Dues / P&L / Tenant Profile /
 * Property Details) to give managers confidence that the numbers they're looking
 * at are fresh — especially when they have multiple tabs/devices open.
 *
 * Props:
 *   • syncedAt   — Date object set by the page after a successful fetch.
 *   • onRefresh  — optional click handler for the refresh icon.
 *   • refreshing — optional bool; if true, the refresh icon spins.
 *
 * The badge re-renders every second to keep the relative time accurate, but
 * only via a single setInterval — no parent re-renders triggered.
 */
import React, { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2 } from 'lucide-react';

const formatRelative = (date) => {
  if (!date) return '—';
  const diff = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diff < 2)       return 'Just now';
  if (diff < 60)      return `${diff}s ago`;
  if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export const LastSyncedBadge = ({ syncedAt, onRefresh, refreshing = false }) => {
  // Force a re-render every second so the relative timestamp stays current.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!syncedAt) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [syncedAt]);

  if (!syncedAt) return null;

  const isFresh = (Date.now() - syncedAt.getTime()) < 5000;

  return (
    <div
      data-testid="last-synced-badge"
      className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 px-2 py-1 rounded-md bg-white border border-gray-200"
    >
      <CheckCircle2 size={11} className={isFresh ? 'text-green-600' : 'text-gray-400'} />
      <span>
        Synced <span className="font-medium text-gray-700">{formatRelative(syncedAt)}</span>
      </span>
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="ml-0.5 p-0.5 rounded hover:bg-gray-100 disabled:opacity-50"
          title="Refresh now"
          data-testid="last-synced-refresh"
        >
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
        </button>
      )}
    </div>
  );
};

export default LastSyncedBadge;
